// =============================================================================
// DRIVE SCROLL RESTORE — W15 (docs/WALK-FINDINGS-2026-07-21.md's late
// addition, his 00:19 essay-walk message): "Save to Drive → reAuth predicted
// correctly ('all good') → but returned to the TOP of the page (scroll
// position lost through the OAuth return leg)."
//
// DriveSaveButton's handleConnect does a SAME-TAB full-page navigation
// (window.location.href = url) to Google's consent screen; the callback
// lands back on the exact same path (exports.routes.ts's
// resolveDriveReturnLocation — a bare `${origin}${path}`, no marker query
// param the way the sign-in flow's session_handoff carries one). That full
// reload throws away every bit of in-memory React state, including scroll
// position — a long essay/résumé read gets dumped back at the top instead of
// where the person was reading when they tapped Save.
//
// NOTE — the actual scrolling element in this app is AppShell's own
// `.scrollArea` div (overflow-y:auto), NEVER `window`/`<body>` (the
// mobile-first phone-frame shell is `overflow:hidden` at the `.shell` level
// — see AppShell.module.css). window.scrollY is always 0 here; any fix that
// reads/writes it would be a silent no-op. CoachMark.tsx already established
// the house idiom for reaching that div from OUTSIDE AppShell without a ref
// — `document.querySelector('[class*="scrollArea"]')` (matches the CSS-
// module-hashed class by its readable prefix) — this module's two call sites
// (DriveSaveButton's capture, AppShell's own restore) reuse that exact
// selector/an owned ref respectively; see each call site's own comment.
//
// Pure marker logic only lives here (this repo's vitest run is plain `node`,
// no jsdom — see driveExportState.test.ts's own header comment) — the actual
// DOM scrollTop read/write is the thin component's job, mirroring
// driveExportState.ts's "pure module decides, component does I/O" split.
//
// sessionStorage, not localStorage (same reasoning as driveExportState.ts's
// ATTEMPT_STORAGE_KEY): tab-scoped, exists specifically to survive the
// same-tab full-page redirect, never meant to persist beyond it.
// =============================================================================

export interface DriveScrollMarker {
  /** window.location.pathname at the moment of capture — the restore side
   *  (resolveScrollRestoreTarget below) only ever applies a marker back to
   *  the SAME path it was captured on, so a marker can never leak its
   *  position onto an unrelated screen (e.g. the person navigates away
   *  entirely instead of following the Drive OAuth round trip). */
  path: string;
  /** The `.scrollArea` element's scrollTop at capture time. */
  scrollTop: number;
  /** Date.now() at capture — bounds how long a marker stays eligible (see
   *  MAX_MARKER_AGE_MS) so an OAuth tab abandoned for a long stretch and
   *  resumed later never hijacks the scroll position on what is by then an
   *  ordinary, unrelated visit. */
  capturedAt: number;
}

const SCROLL_MARKER_KEY = 'tl-drive-scroll-anchor';

/** A marker older than this is treated as though it were never written —
 *  generous enough to cover a slow, hands-on Google consent screen, tight
 *  enough that a tab left open and revisited hours later doesn't hijack the
 *  scroll position on an otherwise-ordinary later visit. */
export const MAX_MARKER_AGE_MS = 10 * 60 * 1000;

function hasSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

/** Call right before the full-page navigate to Google (DriveSaveButton's
 *  handleConnect) — best-effort only, same posture as
 *  writeDriveConnectAttemptStorage: worst case a write fails silently and
 *  the restore below simply finds nothing, never a crash on the way out the
 *  door. */
export function writeDriveScrollMarker(path: string, scrollTop: number, now: number): void {
  if (!hasSessionStorage()) return;
  try {
    const marker: DriveScrollMarker = { path, scrollTop, capturedAt: now };
    window.sessionStorage.setItem(SCROLL_MARKER_KEY, JSON.stringify(marker));
  } catch {
    // Best-effort — see module header.
  }
}

/** Read back whatever was last written, tolerating a missing/corrupted value
 *  the same way readDriveConnectAttemptStorage does — never throws, never
 *  invents a marker that wasn't really stored. */
export function readDriveScrollMarker(): DriveScrollMarker | null {
  if (!hasSessionStorage()) return null;
  try {
    const raw = window.sessionStorage.getItem(SCROLL_MARKER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DriveScrollMarker>;
    if (
      typeof parsed.path !== 'string' ||
      typeof parsed.scrollTop !== 'number' ||
      typeof parsed.capturedAt !== 'number'
    ) {
      return null;
    }
    return { path: parsed.path, scrollTop: parsed.scrollTop, capturedAt: parsed.capturedAt };
  } catch {
    return null;
  }
}

/** Single-use: call once the marker has been consumed (applied or rejected)
 *  so an ordinary later reload of the same path never re-applies a stale
 *  position. Best-effort, mirrors the read/write guards above. */
export function clearDriveScrollMarker(): void {
  if (!hasSessionStorage()) return;
  try {
    window.sessionStorage.removeItem(SCROLL_MARKER_KEY);
  } catch {
    // Best-effort — see module header.
  }
}

/**
 * THE pure decision: given whatever marker was read (or null), the path
 * we've actually landed back on, and the current time, should we restore —
 * and to what scrollTop? Isolated from all I/O so it's directly unit
 * testable without sessionStorage or a DOM (this is the "pure marker logic"
 * this lane's brief calls out).
 *
 * Returns null (do nothing) when: there is no marker, the marker was
 * captured on a DIFFERENT path (never apply a foreign page's scroll
 * position here), the marker has aged past MAX_MARKER_AGE_MS, or the
 * timestamp is corrupted into the future (clock skew / bad data — never
 * trust it over a plain "don't restore").
 */
export function resolveScrollRestoreTarget(
  marker: DriveScrollMarker | null,
  currentPath: string,
  now: number,
): number | null {
  if (!marker) return null;
  if (marker.path !== currentPath) return null;
  const age = now - marker.capturedAt;
  if (age < 0 || age > MAX_MARKER_AGE_MS) return null;
  return marker.scrollTop;
}
